'use client';

import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Button,
  ButtonGroup,
  Flex,
  Input,
  Separator,
  Text,
} from '@/_components';
import { setSignInMethod } from '@/_api/users';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { deformatPhoneNumber, formatPhoneNumber } from '@/_utils/phoneNumbers';
import { isSameOriginUrl } from '@/_utils/validations';
import { useSignInWithCode } from '@/_hooks/useSignInWithCode';
import type { SetPhoneNumberProps } from './SetPhoneNumber.types';

export const SetPhoneNumber: FC<SetPhoneNumberProps> = ({ onBackOverride }) => {
  const { previousLoginState, setLoginState } = useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { setUser, user, token } = useContext(UserContext);
  const { push } = useRouter();
  const { shouldShow2FA, send2FAandSetState } = useSignInWithCode();
  const searchParams = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');

  const onBack = () => {
    if (onBackOverride) return onBackOverride();

    setLoginState(previousLoginState || 'login');
  };

  useEffect(() => {
    if (phoneError) {
      addToast('error', phoneError);
    }
  }, [addToast, phoneError]);

  const validatePhone = (phoneNumber: string) => {
    let error = '';

    if (!phoneNumber) error = 'Phone is required';

    const rawPhone = deformatPhoneNumber(phoneNumber);

    if (rawPhone.length !== 10) {
      error = 'Phone number must be 10 digits';
    }

    setPhoneError(error);
    return error;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const error = validatePhone(phone);

    if (error) return setIsLoading(false);

    setIsLoading(true);

    const rawPhone = deformatPhoneNumber(phone);

    try {
      const redirect: string | null = searchParams.get('redirect');
      const { error } = await setSignInMethod({
        signInMethod: 'sms',
        phoneNumber: rawPhone,
        token,
      });

      if (!error) {
        setUser({ ...user!, set_password_required: false });
      }

      if (shouldShow2FA) {
        return send2FAandSetState();
      }

      if (!error) {
        const redirectUrl =
          redirect && isSameOriginUrl(redirect) ? redirect : '/';
        return push(redirectUrl);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onPhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(event.target.value);
    setPhone(formattedPhone);
  };

  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Text type="small">
          Please enter your phone number so that you can sign in next time.
        </Text>
        <Input
          label="Phone"
          value={phone}
          onChange={onPhoneChange}
          maxLength={14}
          placeholder="Enter your phone number"
          mask="(___) ___-____"
          type="tel"
        />
        <Separator isLight={true} />
        <ButtonGroup align="space-between" className="flex-row">
          <Button
            iconLeft="arrow-left"
            intent="ghost"
            onClick={onBack}
            type="button"
            value="Back"
          />
          <Button
            isDisabled={isLoading}
            isLoading={isLoading}
            intent="primary"
            type="submit"
            value="Submit"
          />
        </ButtonGroup>
        <Text type="small" color="secondary">
          * By providing this phone number, you agree to receive messages from
          us. Message and data rates may apply. Message frequency varies.
        </Text>
      </Flex>
    </form>
  );
};
